/**
 * Page copy for /services/qa-architecture-engineering.
 *
 * Source: page-test-architecture.md, supplied by the client.
 *
 * Test Architecture & QA Platform Engineering is WeAreQA's fourth service. It
 * appears in the superseded v1 sitemap but not in the approved homepage design,
 * which sets three engagement models — so the homepage still says three. The
 * client has confirmed it is a distinct service.
 *
 * The source specifies /services/test-automation-architecture; the client chose
 * /services/qa-architecture-engineering instead.
 *
 * Its "Related" list points at /services/ai-testing-services and
 * /services/qa-consulting, the superseded v1 slugs. They are mapped to the real
 * routes here.
 *
 * Every `[NEEDS: …]` gap in the source renders as a labelled placeholder.
 */

export const heading = "Test Architecture & QA Platform Engineering";

export const seo = {
  title: "Test Automation Architecture & QA Platform Engineering",
  description:
    "Fixed-scope engagements rebuilding automation frameworks, test data platforms, CI/CD orchestration: Playwright, Selenium, Cypress, API and contract layers.",
} as const;

/**
 * §1.
 *
 * OPEN QUESTION, NO LONGER SHOWN ON THE PAGE. The source flagged the opener as
 * inferred and asked: "Prospect language — what do they say to you before they
 * buy this?" That is a research question addressed to the client, not missing
 * content, so it rendered as a dashed box under the hero CTAs asking a visitor
 * something only the client can answer. Removed at the client's request.
 *
 * The question still stands and is worth answering. The opener is good copy —
 * "adding people makes it worse more slowly" earns its place — but it is
 * written as OUR diagnosis, not the buyer's complaint. A CTO is more likely to
 * arrive saying "regression takes all night" or "nobody trusts the failures"
 * than "we have an architecture problem"; the second paragraph is where the
 * page should be allowed to reframe it, and it already does.
 *
 * If a real prospect sentence ever lands, it replaces `opening[0]` and nothing
 * else needs to move.
 */
export const hero = {
  opening: [
    "Your regression suite runs overnight, and the failures nobody trusts outnumber the ones that matter. Adding tests makes it worse. Adding people makes it worse more slowly.",
    "That's not a testing problem. It's an architecture problem, and no amount of additional test cases will fix it.",
  ],
  sub: "Fixed-scope engagements that rebuild how your tests are structured, where your test data comes from, and how your pipeline runs them.",
} as const;

export const symptoms = {
  heading: "Why is my test suite so slow?",
  lead: "Slow suites are a symptom. So are flaky ones. The underlying cause is almost always structural, and it shows up in recognizable ways:",
  items: [
    {
      lead: "The suite gets slower every sprint",
      tail: " and nobody can say which tests are load-bearing.",
    },
    {
      lead: "Failures get re-run rather than investigated.",
      tail: " Once a team stops believing a red build, the suite has stopped doing its job even when it passes.",
    },
    {
      lead: "Tests can only run one at a time",
      tail: ", because they share test data and step on each other.",
    },
    {
      lead: "A new engineer takes weeks to add a test",
      tail: " — the framework has accumulated conventions nobody wrote down.",
    },
    {
      lead: "Most coverage sits in the UI layer",
      tail: ", not because those journeys matter most, but because that's where the framework made it easy.",
    },
    {
      lead: "Nobody will delete a test",
      tail: ", because nobody can tell what it covers.",
    },
  ],
  close:
    "If three or more of those are true, more tests will make the situation worse, not better.",
} as const;

export const workstreams = {
  heading: "What we do",
  lead: "Three workstreams. Most engagements need one of them badly and the other two partially.",
  items: [
    {
      title: "Framework architecture",
      icon: "cube",
      body: [
        "Rebuilding a suite, or migrating one — Selenium to Playwright, or an in-house framework nobody maintains any more. The decision that matters isn't the tool. It's what belongs at each layer.",
        "Most suites are top-heavy: coverage sits in the browser because that's where the original framework made writing tests easy. Moving that coverage down — into API, contract, and component tests — is what turns an overnight run into a coffee-break one. The browser tests that survive are the handful of journeys that genuinely need a browser.",
      ],
    },
    {
      title: "Test data and environments",
      icon: "database",
      body: [
        "Shared, mutable test data is the single most common cause of flakiness we find. Tests pass alone and fail together, and teams learn to re-run rather than fix.",
        "We build provisioning that gives each run its own isolated data, seeded to a known state and torn down after. That's what makes parallel execution possible, and parallel execution is where most of the runtime reduction actually comes from.",
      ],
    },
    {
      title: "CI/CD test orchestration",
      icon: "gears",
      body: [
        "Where tests run, when, in what order, and what they gate. Not everything belongs on every commit. A pipeline that runs the full suite on every push trains engineers to ignore it.",
        "We define the tiers — commit, merge, release — and wire quality gates that block for real reasons and only real reasons.",
      ],
    },
  ],
} as const;

export const engagement = {
  heading: "How it works",
  lead: "Fixed scope. Four to six weeks. Defined deliverables and a handover at the end.",
  columns: ["Weeks", "What happens"],
  rows: [
    [
      "1",
      "Assess the current suite — runtime, flake rate, layer distribution, data dependencies",
    ],
    ["2", "Architecture decisions and the migration path, agreed with your team"],
    [
      "3–5",
      "Reference implementation: the new structure, working, on a real slice of your suite",
    ],
    ["5–6", "Handover — documentation, conventions, and your engineers building on it"],
  ],
  calloutLead: "What you get at the end is a working pattern and a route to follow, not a finished migration.",
  calloutBody:
    " A full migration of a mature suite takes months and it should be your team doing it — they're the ones who'll live with it. Our job is to make sure they're migrating toward something that holds up.",
  frameworkLead: "This engagement sits in the Platform layer of the ",
  frameworkLink: { label: "AI-Native Delivery Framework", href: "/services/ai-driven-validation#framework-heading" },
  frameworkTail:
    ". It's usually the prerequisite that makes the AI capability layer worth building.",
} as const;

export const stack = {
  /** Source heading was "The stack"; renamed at the client's direction. */
  heading: "Tech Stack",
  groups: [
    { label: "Browser", value: "Playwright, Selenium, Cypress", gap: null },
    {
      label: "API and contract",
      value: "REST Assured, Pact, Postman/Newman, supertest",
      gap: null,
    },
    {
      label: "CI/CD",
      value: "GitHub Actions, GitLab CI, Jenkins, Azure DevOps",
      gap: null,
    },
    { label: "Test management", value: "TestRail, Zephyr, Jira, Confluence", gap: null },
  ],
  close:
    "We don't have a preferred framework we push onto every client. The right choice depends on what your engineers already know, what your application is built in, and what your CI can run. Migrating to a tool your team doesn't want to maintain is how suites get abandoned twice.",
} as const;

export const deliverables = {
  heading: "What you get",
  items: [
    {
      lead: "Current-state assessment",
      tail: " — runtime, flake rate, layer distribution, and where the time actually goes",
    },
    {
      lead: "Architecture decision record",
      tail: " — what goes at each layer and why, written so it survives your next hire",
    },
    {
      lead: "Reference implementation",
      tail: " — the new structure working against a real slice of your suite",
    },
    { lead: "Test data provisioning", tail: " — isolated, seeded, repeatable" },
    { lead: "Pipeline configuration", tail: " — tiered execution and quality gates" },
    {
      lead: "Migration plan",
      tail: " — sequenced, with effort estimates your team can plan against",
    },
    { lead: "Handover sessions", tail: " — your engineers building on it before we leave" },
  ],
} as const;

/**
 * §8. NO PRICE, BY DECISION — the client does not want a figure on the page.
 *
 * The heading changed from "Starting from", which promised one. NEW COPY
 * pending sign-off, as are the five term values; `body` is the source's,
 * unchanged.
 *
 * WHY TERMS AND NOT PRICE DRIVERS. The sister page,
 * /services/intelligent-quality-operations, answers the same empty slot with
 * "what sets the number", because it is a rolling retainer and the number
 * genuinely varies. This engagement is fixed scope at a fixed price, so the
 * useful question is not "how much" but "what am I committing to".
 *
 * These four restate `body`, which sits beside them. Commercial terms are worth
 * saying twice — buyers skim prose and stop at tables, the same reasoning behind
 * the terms table on the Quality Operations page.
 *
 * A FIFTH ROW WAS REMOVED at the client's request: "Not included — the full
 * migration. That's your team's, and it takes months." It was the only row
 * carrying information not already in the section, so the table is now purely a
 * scannable restatement of the paragraph beside it rather than an addition to
 * it. The point itself is not lost — §4's callout still says a full migration
 * is the client's team's work — but it no longer appears where a buyer scanning
 * the commercial terms would meet it.
 */
export const pricing = {
  heading: "The terms",
  body: "Fixed scope, fixed price, four to six weeks. You know the cost before it starts and there's no open-ended engagement to exit.",
  terms: [
    { label: "Scope", value: "Fixed, agreed before we start" },
    { label: "Duration", value: "Four to six weeks" },
    { label: "Price", value: "Fixed at scope" },
    { label: "Ends with", value: "Handover, not a dependency" },
  ],
} as const;

export const related = {
  heading: "Related",
  items: [
    {
      label: "The AI-Native Delivery Framework",
      href: "/services/ai-driven-validation#framework-heading",
      note: "where this sits in the wider model",
    },
    {
      label: "AI-Driven Validation",
      href: "/services/ai-driven-validation",
      note: "for teams whose architecture is sound but whose test design is manual",
    },
    {
      label: "QA Consulting & Modernization",
      href: "/services/qa-consulting-modernization",
      note: "if you're not yet sure architecture is the problem",
    },
  ],
} as const;
